import { Injectable } from '@angular/core';

import { ApplicationError } from '../../error-handler/error-handler.service';
import { ConfigParserService } from '../services/config-parser.service';
import { JsonElementParser } from '../utilities/json-element-parser.utility';
import { setCdnDomain } from '../utilities/set-cdn-domain.utility';

export enum AuthType {
    MSA = 0,
    OrgId = 1,
    Unknown = -1
}

export interface ClientStartupConfig {
    angularDebugInfoEnabled: boolean;
    authType: AuthType;
    authTypeString: string;
    authVersion: string;
    automatedTestRunning: boolean;
    cdnDomain: string;
    chunkLoadStrategy: 'noop' | 'idle-callback';
    dataBoundary: string;
    features: string;
    fixedConsumerTenantId: string;
    isV2Auth: boolean;
    isPipl: boolean;
    loginHint: string;
    logPiiAllowed: boolean;
    logPiiInClearText: boolean;
    objectId: string;
    puid: string;
    resourceLoggingEnabled: boolean;
    sanitizeMetrics: boolean;
    sharedClientStartupContext: SharedClientStartupContext;
    tenantId: string;
    userCountryCode: string;
    userPrincipalName: string;
    officeStartCdnDomain: string;
    portalPreloadUrl: string;
}

export interface SharedClientStartupContext {
    ariaCollectorUrl: string;
    ariaTenant: string;
    buildDateUtc: string;
    buildId: string;
    clientOTelMediumSchedulePriorityEnabled: boolean;
    clientOTelHighSchedulePriorityEnabled: boolean;
    clientOTelImmediateSchedulePriorityEnabled: boolean;
    cookieConsentRequired: boolean;
    corpNet: boolean;
    correlationId: string;
    deploymentEnvironment: string;
    devEnvironment: string;
    ecsRing: string;
    flights: string;
    geoName: string;
    domainOrigin: string;
    oTelAriaTenant: string;
    oTelAppName: string;
    oTelAppPlatform: string;
    oTelClickEnabled: boolean;
    oTelDiagnosticEnabled: boolean;
    oTelEnabled: boolean;
    oTelErrorEnabled: boolean;
    oTelEudbEndpointEnabled: boolean;
    oTelFeatureEnabled: boolean;
    oTelImpressionEnabled: boolean;
    oTelNameSpace: string;
    oTelPageViewEnabled: boolean;
    oTelPerfEnabled: boolean;
    oTelRequestEnabled: boolean;
    // TODO: Move Otel Sampling variables if not used in standalone logger
    oTelSamplingEnabled: boolean;
    oTelSamplingPerformanceEnabled: boolean;
    oTelSamplingRequestEnabled: boolean;
    oTelWin11DetectionEnabled: boolean;
    pwaV2TokenEnabled: boolean;
    requestOrigin: string;
    sessionId: string;
    testTraffic: boolean;
    useMruS2SFlow: boolean;
    useFeedApi: boolean;
}

let startupConfigLoaded: boolean;
let startupConfig: ClientStartupConfig;

export function getStartupConfiguration(configParserService?: ConfigParserService) {
    // Failure to parse clientStartupConfiguration is catastrophic so attempt it only once
    if (!startupConfigLoaded) {
        startupConfigLoaded = true;
        try {
            if (!configParserService) {
                startupConfig = JsonElementParser.parseJsonElementWithId('clientStartupConfiguration');
            } else {
                startupConfig = configParserService.parse<ClientStartupConfig>('clientStartupConfiguration');
            }

        } catch (ex) {
            throw new ApplicationError('Failure to parse clientStartupConfiguration from response', ex);
        }

        if (!startupConfig) {
            throw new ApplicationError('clientStartupConfiguration is not present in response');
        }

        const authTypeString = startupConfig.authTypeString;
        if (authTypeString === 'MSA') {
            startupConfig.authType = AuthType.MSA;

            // In PWA MSA, add puid and UPN retrieved from WAM to config.
            if (window.pwaCurrentUserId && window.pwaCurrentUserId.indexOf('consumers_') === 0) {
                startupConfig.puid = window.pwaCurrentUserId.substring('consumers_'.length);
            }

            if (window.userPrincipalName) {
                startupConfig.userPrincipalName = window.userPrincipalName;
                startupConfig.loginHint = window.userPrincipalName;
            }
        } else if (authTypeString === 'OrgId') {
            startupConfig.authType = AuthType.OrgId;
        } else {
            startupConfig.authType = AuthType.Unknown;
        }

        if (startupConfig.authVersion === '2.0') {
            startupConfig.isV2Auth = true;
        } else {
            startupConfig.isV2Auth = false;
        }
    }

    setCdnDomain(startupConfig.cdnDomain);
    return startupConfig;
}

/**
 * This configuration is used for any server side configuration variables that need to be exposed in the first flush
 * It allows the bootstrapping process to occur during the first flush, but when one injects another service into a
 * service used in the first flush, care must be taken to ensure that that service doesn't rely on configurations
 * that are passed down in the second flush.
 */
@Injectable({
    providedIn: 'root'
})
export class ClientStartupConfigService {
    public readonly config: ClientStartupConfig;

    constructor(configParserService: ConfigParserService
    ) {
        this.config = getStartupConfiguration(configParserService);
    }
}
